/**
 * Curated list of countries that the postal-code lookup supports.
 *
 * Zippopotam.us publishes data for ~70 countries; we ship the subset
 * most likely to come up in research-platform onboarding. Each entry
 * is ISO 3166-1 alpha-2 (the format Zippopotam accepts in its URL
 * path) paired with the display name.
 *
 * The country field in our DB stays free-text (so a user *can* type
 * a country we don't list); when they pick one of these, postal-code
 * lookup runs. When they type a custom value, the form still works,
 * just without the autofill.
 *
 * Sorted alphabetically by display name so the Autocomplete shows
 * predictable order. Default selection in the UI is "United States"
 * — the most likely participant location for 10NEWTONS.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code (uppercase). */
  code: string;
  /** Display name. */
  name: string;
}

export const SUPPORTED_COUNTRIES: readonly Country[] = [
  { code: 'AR', name: 'Argentina' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CA', name: 'Canada' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RU', name: 'Russia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TR', name: 'Turkey' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

const _byName: Map<string, Country> = new Map(
  SUPPORTED_COUNTRIES.map((c) => [c.name.toLowerCase(), c]),
);
const _byCode: Map<string, Country> = new Map(
  SUPPORTED_COUNTRIES.map((c) => [c.code.toLowerCase(), c]),
);

/**
 * Resolve a free-text country string to a supported Country, or null.
 *
 * Matches case-insensitively against the display name or the ISO
 * code, and tolerates a small set of common aliases ("USA", "UK").
 * Anything we don't recognise returns null, and the caller should
 * fall back to "no lookup" — never blow up the form.
 */
export function findCountry(input: string): Country | null {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;

  const aliases: Record<string, string> = {
    usa: 'us',
    'u.s.': 'us',
    'u.s.a.': 'us',
    america: 'us',
    uk: 'gb',
    'u.k.': 'gb',
    'great britain': 'gb',
    england: 'gb',
    deutschland: 'de',
  };

  const aliased = aliases[trimmed];
  if (aliased && _byCode.has(aliased)) {
    return _byCode.get(aliased) ?? null;
  }
  return _byName.get(trimmed) ?? _byCode.get(trimmed) ?? null;
}
